import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Paperclip, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isImage(ct = "") {
  return /image\//i.test(ct);
}

/**
 * Lista compacta de anexos de uma tarefa + botão para adicionar.
 * Props:
 *  - task: objeto da tarefa
 *  - onChange(updatedAttachments): callback após upload/delete
 *  - canEdit: bool — se false, só lista (sem botões)
 */
export default function TaskAttachments({ task, onChange, canEdit = true }) {
  const { apiCall } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const attachments = task.attachments || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/tasks/${task.task_id}/attachments`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange?.([...(attachments || []), res.data]);
      toast.success("Arquivo anexado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Remover "${att.filename}"?`)) return;
    try {
      await apiCall("delete", `/tasks/${task.task_id}/attachments/${att.attachment_id}`);
      onChange?.(attachments.filter((a) => a.attachment_id !== att.attachment_id));
      toast.success("Removido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  if (!canEdit && attachments.length === 0) return null;

  return (
    <div className="mt-2" data-testid={`task-attachments-${task.task_id}`}>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1">
          {attachments.map((a) => (
            <div
              key={a.attachment_id}
              className="group relative border border-border rounded-md overflow-hidden bg-card"
              style={{ width: 64, height: 64 }}
              data-testid={`task-att-${a.attachment_id}`}
            >
              <a href={a.url} target="_blank" rel="noopener noreferrer" title={a.filename}>
                {isImage(a.content_type) ? (
                  <img
                    src={a.url}
                    alt={a.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-secondary/30">
                    <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-[8px] mt-0.5 truncate w-full px-0.5 text-center">{a.filename}</span>
                  </div>
                )}
              </a>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleDelete(a)}
                  className="absolute top-0 right-0 p-0.5 bg-black/60 text-white rounded-bl opacity-0 group-hover:opacity-100 transition"
                  title="Remover"
                  data-testid={`task-att-delete-${a.attachment_id}`}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.8} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-xs h-7 px-2"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`task-att-upload-${task.task_id}`}
          >
            <Paperclip className="h-3 w-3 mr-1" strokeWidth={1.5} />
            {uploading ? "Enviando…" : "Anexar foto/gif/documento"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            data-testid={`task-att-input-${task.task_id}`}
          />
        </>
      )}
    </div>
  );
}
